/**
 * Блок «Экономика региона» (US-35.1 FR-4) — четыре карточки в порядке
 * выдачи: промышленность, сельское и лесное хозяйство, инвестиции,
 * строительство. Порядок задаёт поле `order` ответа, а не экран: места
 * ВРП (1) и МСП (6) зарезервированы второй волной, и их карточки встанут
 * на свои места, а не в конец блока (US-36.1 FR-7.1).
 *
 * Блок **заменяет** виджет «Экономическое развитие» (топ-4 отрасли СЭР):
 * оба показывали отраслевые объёмы с ИФО, и держать их одновременно —
 * значит показывать одно и то же дважды (FR-4.2).
 *
 * У каждой карточки своя подпись отчётного периода (FR-4.7): публикации
 * выходят в разные дни, и общей подписи у блока быть не может.
 */
import { useTranslation } from 'react-i18next';

import { EconomyIndicatorCard } from './EconomyIndicatorCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import type { RegionEconomyResponse } from '../../../types/analytics';

interface RegionEconomyBlockProps {
  data: RegionEconomyResponse | null;
  loading: boolean;
}

export const RegionEconomyBlock = ({
  data,
  loading,
}: RegionEconomyBlockProps) => {
  const { t } = useTranslation();

  // Блок, выключенный супер-администратором, и неприменимый уровень КАТО —
  // не «нет данных»: блока нет вовсе (FR-9.3, US-36.1 FR-6.1).
  if (data !== null && (!data.widget_enabled || !data.level_supported)) {
    return null;
  }

  return (
    <section className="space-y-3.5" data-testid="block-economy-region">
      <div className="flex items-center gap-4 pt-2">
        <h3 className="shrink-0 text-lg font-extrabold">
          {t('analytics.economy.blocks.regionTitle')}
        </h3>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
      </div>

      {loading ? (
        <BlockLoader testId="economy-region-loader" />
      ) : (data?.indicators.length ?? 0) === 0 ? (
        <p
          className="text-sm lowercase text-text-muted"
          data-testid="block-placeholder"
        >
          {t('common.noData')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
          {data?.indicators.map((indicator) => (
            <EconomyIndicatorCard
              key={indicator.key}
              indicator={indicator}
              testId={`economy-region-${indicator.key}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
